import { Drawer, Pagination, Table, ModalCommon } from '@/components/commons';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Input, TableColumnsType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CategoryForm } from '../Drawers/CategoryForm';
import { formSchemaFn } from '../Drawers/CategoryForm/formSchemaFn';
import { CategoryService } from '@/services/category';
import {
  ICreateCategoryRequest,
  IGetListCategoryRequest,
  IUpdateCategoryRequest,
} from '@/types/request/category';
import { IGetDetailCategoryResponse } from '@/types/response/category';
import { ERROR_MESSAGE } from '@/utils/constants/messages';

interface IProps {
  title?: string;
}

interface IParams extends IGetListCategoryRequest {
  name?: string;
  limit: number;
  page: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export const CategoryList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailCategoryResponse>();
  const [isNotPermissionEdit, setIsNotPermissionEdit] =
    useState<boolean>(false);
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
    sortBy: 'sortOrder',
    sortDirection: 'ASC',
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.CATEGORY.GET_LIST, params],
    () => CategoryService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(
      formSchemaFn(!!currentId, isNotPermissionEdit, currentId || undefined),
    ),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, watch, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({});
  };

  const { mutate: createCategory, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateCategoryRequest) => CategoryService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'カテゴリが正常に作成されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.CATEGORY.GET_ALL]);
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.CATEGORY.GET_DETAIL.replace(':id', String(currentId))],
    () => CategoryService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailCategoryResponse>) => {
        const data = response?.data;
        setDetail(data);
        reset({
          name: data?.name,
          sortOrder: data?.sortOrder,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const { mutate: updateCategory, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdateCategoryRequest; id: number }) =>
      CategoryService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'カテゴリが正常に更新されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.CATEGORY.GET_ALL]);
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { mutate: deleteCategory, isLoading: isLoadingDelete } = useMutation(
    (id: number) => CategoryService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'カテゴリが正常に削除されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.CATEGORY.GET_ALL]);
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: any) => {
        // Check if the error is because the category is in use
        if (error?.response?.data?.code === 'CATEGORY_IN_USE') {
          const categoryName = detail?.name || 'カテゴリ';
          const message = ERROR_MESSAGE.CATEGORY_IN_USE.replace(
            '{$Item name$}',
            categoryName,
          );
          notificationMessage({
            message,
            type: NOTIFY_STATUS.ERROR,
          });
        } else {
          handleErrorCommon(error, setError);
        }
      },
    },
  );

  const onChangePagination = (page: number) => {
    setParams({ ...params, page });
  };

  useEffect(() => {
    setIsNotPermissionEdit(watch('isDisabled'));
    if (isNotFoundDetail) {
      setOpenDrawer(false);
      refetch();
    }
  }, [watch('isDisabled'), isNotFoundDetail]);

  const columns: TableColumnsType<any> = [
    {
      title: 'カテゴリー名',
      dataIndex: 'name',
      align: 'left',
      width: '50%',
      render: (value) => value,
    },
    {
      title: '並び順指定',
      dataIndex: 'sortOrder',
      align: 'center',
      width: '25%',
      render: (value) => value,
    },
    {
      title: '作成日時',
      dataIndex: 'createdAt',
      align: 'center',
      width: '25%',
      render: (value) => value && moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  const handleDelete = () => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteCategory(detail!.id),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  return (
    <div className='p-post-list'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <div className='flex justify-between'>
          <div className='flex justify-start gap-[6px] items-center'>
            <Input
              className='w-[300px]'
              prefix={<SearchOutlined className='text-[20px]' />}
              placeholder='カテゴリー名'
              onChange={(e) => {
                setParams({ ...params, page: 1, name: e.target.value });
              }}
            />
          </div>
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                setOpenDrawer(true), reset({});
              }}
            >
              新規作成
            </Button>
          </header>
        </div>
        <div className='flex justify-between flex-col'>
          <Table
            loading={isFetching}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
            })}
            locale={{ emptyText: 'カテゴリはまだありません' }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={!currentId ? 'カテゴリ新規作成' : 'カテゴリ詳細'}
        onOk={handleSubmit((values: ICreateCategoryRequest) =>
          currentId
            ? updateCategory({
                body: values as IUpdateCategoryRequest,
                id: currentId,
              })
            : createCategory(values),
        )}
        onCancel={handleCancel}
        onDelete={handleDelete}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={
          currentId
            ? isFetchingDetail || isLoadingUpdate || isLoadingDelete
            : isLoadingCreate
        }
        isDeleted={!!currentId}
      >
        <FormProvider {...methods}>
          <CategoryForm isLoading={isFetchingDetail} />
        </FormProvider>
      </Drawer>
    </div>
  );
};
